import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

// Firebase
import { onDisconnect, onValue, push, ref, remove, serverTimestamp, set } from 'firebase/database';
import { realTimeDb } from 'firebaseConfig';

// Context
import { useUser } from 'context/UserContext';

const APP_PRESENCE_ROOT = 'appPresence';
const PRESENCE_TTL_MS = 90 * 1000;
const PRESENCE_HEARTBEAT_MS = 25 * 1000;

const normalizeKey = (value) => String(value || '').trim();

const getMillis = (value) => {
  const asNumber = Number(value);
  return Number.isFinite(asNumber) ? asNumber : 0;
};

const OnlineContext = createContext(null);

export const OnlineProvider = ({ children }) => {
  const { user } = useUser();
  const [onlineUserKeys, setOnlineUserKeys] = useState([]);

  const userKey = useMemo(() => normalizeKey(user?.key), [user?.key]);

  // Subscribes to all app presence rows and derives a fresh online user key list.
  // Wait for a resolved user key so we do not attach unauthenticated listeners
  // that can fail under auth-gated rules and never recover.
  useEffect(() => {
    if (!userKey) {
      setOnlineUserKeys([]);
      return () => {};
    }

    const presenceRef = ref(realTimeDb, APP_PRESENCE_ROOT);

    const unsubscribe = onValue(
      presenceRef,
      (snapshot) => {
        const now = Date.now();
        const nextKeys = [];

        snapshot.forEach((userSnap) => {
          const candidateUserKey = normalizeKey(userSnap.key);
          if (!candidateUserKey) return;

          const connectionsSnap = userSnap.child('connections');
          let hasFreshConnection = false;

          connectionsSnap.forEach((connectionSnap) => {
            if (hasFreshConnection) return;

            const value = connectionSnap.val() || {};
            const updatedAtMs = getMillis(value?.updatedAt);
            const isFresh = updatedAtMs <= 0 || (now - updatedAtMs) <= PRESENCE_TTL_MS;

            if (isFresh) {
              hasFreshConnection = true;
            }
          });

          if (hasFreshConnection) {
            nextKeys.push(candidateUserKey);
          }
        });

        setOnlineUserKeys(Array.from(new Set(nextKeys)));
      },
      (error) => {
        console.error('[OnlineContext] failed to subscribe to app presence:', error);
        setOnlineUserKeys([]);
      }
    );

    return () => unsubscribe && unsubscribe();
  }, [userKey]);

  // Maintains this browser tab's connection presence node.
  useEffect(() => {
    if (!userKey) {
      return () => {};
    }

    const userConnectionsRef = ref(realTimeDb, `${APP_PRESENCE_ROOT}/${userKey}/connections`);
    const connectionRef = push(userConnectionsRef);
    const connectedRef = ref(realTimeDb, '.info/connected');

    let heartbeatTimer = null;

    const writePresence = async () => {
      await set(connectionRef, {
        updatedAt: serverTimestamp(),
      });
    };

    const stopHeartbeat = () => {
      if (!heartbeatTimer) return;
      clearInterval(heartbeatTimer);
      heartbeatTimer = null;
    };

    const startHeartbeat = () => {
      if (heartbeatTimer) return;

      heartbeatTimer = setInterval(() => {
        writePresence().catch((error) => {
          console.error('[OnlineContext] heartbeat write failed:', error);
        });
      }, PRESENCE_HEARTBEAT_MS);
    };

    const unsubscribe = onValue(connectedRef, async (snapshot) => {
      if (snapshot.val() !== true) {
        stopHeartbeat();
        return;
      }

      try {
        await onDisconnect(connectionRef).remove();
        await writePresence();
        startHeartbeat();
      } catch (error) {
        console.error('[OnlineContext] failed to set presence:', error);
      }
    });

    return () => {
      stopHeartbeat();
      unsubscribe && unsubscribe();
      onDisconnect(connectionRef).cancel().catch(() => {});
      remove(connectionRef).catch(() => {});
    };
  }, [userKey]);

  const onlineUserKeySet = useMemo(() => {
    return new Set(onlineUserKeys);
  }, [onlineUserKeys]);

  const isUserOnline = useCallback((candidateKey) => {
    const key = normalizeKey(candidateKey);
    return Boolean(key) && onlineUserKeySet.has(key);
  }, [onlineUserKeySet]);

  const value = useMemo(() => ({
    onlineUserKeys,
    isUserOnline,
  }), [onlineUserKeys, isUserOnline]);

  return (
    <OnlineContext.Provider value={value}>
      {children}
    </OnlineContext.Provider>
  );
};

export const useOnline = () => {
  const context = useContext(OnlineContext);
  if (!context) {
    throw new Error('useOnline must be used within OnlineProvider');
  }
  return context;
};

export default OnlineContext;
